import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type KnownRole = 'SUPER_ADMIN' | 'ADMIN' | 'COMMERCIAL' | 'OPERATIONS' | 'JURISTE' | 'VALIDATEUR' | 'CLIENT';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: KnownRole;
  additional_roles: KnownRole[];
  avatar_url?: string | null;
  must_change_password?: boolean;
  client?: {
    id: string;
    company_name: string;
    subscription?: { plan: string; status: string; hours_included: number; hours_used: number } | null;
    referent?: unknown;
  } | null;
  juriste?: { id: string; target_hours: number; is_available: boolean } | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  activeRole: KnownRole | null;
  isAuthenticated: boolean;
  setAuth: (user: User, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (updates: Partial<User>) => void;
  switchProfile: (role: KnownRole) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      activeRole: null,
      isAuthenticated: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true, activeRole: user.role }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      updateUser: (updates) =>
        set(state => ({ user: state.user ? { ...state.user, ...updates } : null })),

      switchProfile: (role) => {
        const { user } = get();
        if (!user) return;
        const allRoles: KnownRole[] = [user.role, ...(user.additional_roles ?? [])];
        if (allRoles.includes(role)) set({ activeRole: role });
      },

      logout: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, activeRole: null }),
    }),
    {
      name: 'lexextern-auth',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeRole: state.activeRole,
      }),
    }
  )
);

// Tous les rôles actifs de l'utilisateur connecté (primaire + additionnels)
export function getAllRoles(user: User | null): KnownRole[] {
  if (!user) return [];
  return [user.role, ...(user.additional_roles ?? [])];
}
